import { ErrorMessage } from '@hookform/error-message'
import { FormProvider } from 'react-hook-form'

import { StarRatingInput } from './StarRatingInput'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '../../Theme'
import { useReviewForm } from '../hooks'
import { REVIEW_LABELS as LABELS } from '../lib'
import { useReviews } from '../state'

import type { ReviewFormProps } from '../types'

/**
 * ReviewForm - Compact review submission form
 *
 * All form state and logic is handled by useReviewForm hook.
 * This component only renders the UI.
 */
export function ReviewForm({ productSku, productName }: ReviewFormProps) {
  const { neededRating, submitReview, isSubmitting } = useReviews()

  const { state, actions, form, validationRules } = useReviewForm({
    productSku,
    neededRating,
    submitReview,
  })

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form

  return (
    <div className="rounded bg-white md:mb-10 mb-5 mt-5 md:mt-10" id="review-form">
      {/* Form */}
      {!state.formSubmitted && (
        <div className="flex items-center justify-center">
          <FormProvider {...form}>
            <form
              className="rounded bg-cards-bg p-4 md:p-5 shadow-custom-box-shadow w-full xl:w-3/5"
              onSubmit={(e) => void handleSubmit(actions.handleSubmit)(e)}
            >
              {/* Header: Product name + Star rating (side by side on desktop) */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div className="flex flex-col gap-0.5">
                  <p className="text-text-color font-main-font text-sm">{LABELS.writeReviewFor}</p>
                  <span className="text-text-color font-main-font font-bold text-lg">
                    {productName}
                  </span>
                </div>

                <div className="flex flex-col items-start md:items-end gap-1">
                  <span className="font-main-font text-sm text-text-color">{LABELS.rateProduct}</span>
                  <div className="flex items-center gap-1">
                    <StarRatingInput
                      key={state.starsKey}
                      starSize={'h-8 w-8'}
                      onStarSelect={actions.setRating}
                    />
                    {state.starRatingError && (
                      <span className="text-sm text-red-500" role="alert">{state.starRatingError}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Nickname + Summary (side by side on desktop) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="nickname"
                    className="font-main-font text-text-color"
                  >
                    {LABELS.nickname}
                  </Label>
                  <Input
                    id="nickname"
                    {...register('nickname', validationRules.nickname)}
                    placeholder={LABELS.nicknamePlaceholder}
                    className="font-main-font text-text-color h-10"
                  />
                  <ErrorMessage
                    errors={errors}
                    name="nickname"
                    render={({ message }) => <span className="text-sm text-red-500" role="alert">{message}</span>}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="summary"
                    className="font-main-font text-text-color"
                  >
                    {LABELS.reviewTitle}
                  </Label>
                  <Input
                    id="summary"
                    {...register('summary', validationRules.summary)}
                    placeholder={LABELS.reviewTitlePlaceholder}
                    className="font-main-font text-text-color h-10"
                  />
                  <ErrorMessage
                    errors={errors}
                    name="summary"
                    render={({ message }) => <span className="text-sm text-red-500" role="alert">{message}</span>}
                  />
                </div>
              </div>

              {/* Review text */}
              <div className="flex flex-col gap-2 mb-4">
                <Label
                  htmlFor="text"
                  className="font-main-font text-text-color"
                >
                  {LABELS.reviewText}
                </Label>
                <Textarea
                  id="text"
                  {...register('text', validationRules.text)}
                  placeholder={LABELS.reviewTextPlaceholder}
                  rows={3}
                  className="font-main-font text-text-color"
                />
                <ErrorMessage
                  errors={errors}
                  name="text"
                  render={({ message }) => <span className="text-sm text-red-500" role="alert">{message}</span>}
                />
              </div>

              {/* Submit Button - right-aligned */}
              <div className="flex justify-end">
                <Button variant="addToCart" size="default" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? LABELS.submitting : LABELS.submitReview}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      )}

      {/* Success Modal */}
      <Dialog open={state.isModalOpen} onOpenChange={(open) => !open && actions.handleCloseModal()}>
        <DialogContent className="w-96">
          <DialogHeader>
            <DialogTitle className="font-main-font">{LABELS.thankYou}</DialogTitle>
            <DialogDescription className="font-main-font">
              {LABELS.reviewSubmitted}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="addToCart"
              size="default"
              onClick={actions.handleCloseModal}
              type="button"
            >
              {LABELS.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
